import { useEffect, useState } from "react"
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from "@mui/material"
import { projectApi } from "../../api"
import { usePermissions } from "../../permissions"
import ClientSelect from "../../components/ClientSelect"

/** Organization 0 is the back office — a tenant like any other, not a blank. */
const BACK_OFFICE_ORG_ID = "0"

const EMPTY = { name: "", code: "", description: "", client_id: "", status: "active", start_date: "", due_date: "" }

export default function ProjectDialog({ open, clients, onClose, onCreated }) {
  const { isBackOffice, organizationId } = usePermissions()
  const [form, setForm] = useState(EMPTY)
  const [codeTouched, setCodeTouched] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    // A real organization id from the start. Back office is "0", not "nothing".
    setForm({ ...EMPTY, client_id: isBackOffice ? BACK_OFFICE_ORG_ID : organizationId || "" })
    setCodeTouched(false)
    setError(null)
  }, [open, isBackOffice, organizationId])

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }))

  /* The code prefixes every task in the project (ONB-14). Derived from the name
     until the user edits it — asking for one up front is friction, and a project
     with no code means its tasks have no readable reference either. */
  const onNameChange = (event) => {
    const name = event.target.value
    setForm((f) => ({
      ...f,
      name,
      code: codeTouched ? f.code : name.replace(/[^A-Za-z0-9]/g, "").slice(0, 4).toUpperCase(),
    }))
  }

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await projectApi.create({
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        description: form.description.trim() || null,
        client_id: form.client_id === "" ? undefined : form.client_id,
        status: form.status,
        start_date: form.start_date || null,
        due_date: form.due_date || null,
      })
      onCreated(result.project)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>New project</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField label="Name" value={form.name} onChange={onNameChange} fullWidth required autoFocus />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Code"
                value={form.code}
                onChange={(e) => {
                  setCodeTouched(true)
                  set("code")(e)
                }}
                fullWidth
                helperText="Prefixes its tasks"
                slotProps={{ input: { sx: { fontFamily: "monospace" } } }}
              />
            </Grid>

            {/* Organization 0 is a real tenant and is listed like any other.
                It used to be a synthetic option with an empty value, which read
                as blank once chosen — indistinguishable from choosing nothing —
                and was then dropped from the request by `|| undefined`. */}
            {isBackOffice && clients.length > 0 ? (
              <Grid size={12}>
                <ClientSelect
                  clients={clients}
                  value={form.client_id}
                  onChange={(id) => setForm((f) => ({ ...f, client_id: id }))}
                  fullWidth
                />
              </Grid>
            ) : null}

            <Grid size={12}>
              <TextField label="Description" value={form.description} onChange={set("description")} fullWidth multiline rows={2} />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Status" value={form.status} onChange={set("status")} fullWidth>
                <MenuItem value="planning">Planning</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="on_hold">On hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Start" type="date" value={form.start_date} onChange={set("start_date")} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Due" type="date" value={form.due_date} onChange={set("due_date")} fullWidth />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !form.name.trim()}>
            {busy ? "Creating…" : "Create project"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
